import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';
import useResponsive from '../utils/useResponsive';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow, A11y, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/a11y';
import 'swiper/css/effect-coverflow';
import 'swiper/css/navigation';
import '../css-overrides/carousel.css';

import WebFeatureLoading from '../common/WebFeatureLoading';
import axios from '../utils/axios';
import ReviewCard from '../common/ReviewCard';
import StarRating from '../common/StarRating/StarRating';

export default function CarouselGroup({ force }) {
    let { groupID } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [group, setGroup] = useState({});
    const [options, setOptions] = useState({ disabled: true });
    const [reviews, setReviews] = useState([]);
    const sliderRef = useRef();

    const isMobile = useResponsive('down', 'sm');
    const isTablet = useResponsive('only', 'sm');
    const isDesktop = useResponsive('up', 'md');

    const updateFrameHostHeight = () => {
        window.parent.postMessage({ feature: 'group-carousel', height: document.body.scrollHeight }, '*');
    };

    const getRatingWord = (stars) => {
        switch (stars) {
            case 5: return 'Excellent';
            case 4: return 'Good';
            case 3: return 'Average';
            case 2: return 'Poor';
            case 1: return 'Awful';
            default: return 'No rating';
        }
    };

    useEffect(() => {
        setLoading(true);
        setError(false);

        console.log(`[Working Feedback Carousel] Getting web feature options with group ID = ${groupID}`);
        axios
            .get(`/group/${groupID}/web-feature-options/carousel${force === 'yes' ? '?force' : ''}`)
            .then((response) => {
                setGroup(response.data._group);
                setOptions(response.data.options);

                if (response.data?.options?.no_package === true) { 
                    console.error('[Working Feedback Carousel] Feature will not be displayed as there is no valid package.');
                }

                if (response.data?.options?.disabled === false) {
                    axios
                        .get(`/group/${groupID}/reviews/public-web-feature-feed`, {
                            headers: {
                                pagination: 'no',
                                limit: 20,
                                google: 'yes',
                                facebook: 'yes',
                                commentless: response.data?.options?.commentlessReviews,
                            },
                        })
                        .then((reviewsResponse) => {
                            setReviews(reviewsResponse.data.reviews);
                        })
                        .catch((err) => {
                            console.error(`[Working Feedback Carousel] Feed error: ${err}`);
                            setError(true);
                        })
                        .finally(() => {
                            setLoading(false);
                        });
                } else {
                    setLoading(false);
                }
            })
            .catch((err) => {
                console.error(`[Working Feedback Carousel] Error: ${err}`);
                setError(true);
            });

        if (window.addEventListener) {
            window.addEventListener(
                'message',
                () => {
                    if (event.data === 'resize') updateFrameHostHeight();
                },
                false
            );
        } else {
            window.attachEvent('onmessage', () => {
                if (event.data === 'resize') updateFrameHostHeight();
            });
        }
    }, [groupID]);

    useEffect(() => {
        if (!loading) setTimeout(updateFrameHostHeight, 500);
    }, [loading]);

    const wrapperClass = options?.disabled ? 'p-0 mb-0' : 'p-2 mb-6';

    return (
        <div className={wrapperClass}>
            {loading && !error && <WebFeatureLoading />}

            {!loading && !error && options?.disabled === true && <></>}

            {!loading && !error && options?.disabled === false && reviews?.length === 0 && <></>}

            {!loading && !error && options?.disabled === false && reviews?.length > 0 && (
                <>
                    <Swiper
                        modules={[A11y, EffectCoverflow, Navigation]}
                        a11y={{ prevSlideMessage: 'Previous review', nextSlideMessage: 'Next review' }}
                        onSwiper={(it) => (sliderRef.current = it)}
                        oldNavigation={isDesktop ? true : false}
                        navigation={false}
                        rewind={true}
                        resizeObserver={true}
                        roundLengths={true}
                        effect={isMobile ? '' : 'coverflow'}
                        coverflowEffect={{ rotate: 0, stretch: 0, depth: 0, scale: 1.0, modifier: 1.0, slideShadows: false }}
                        spaceBetween={10}
                        slidesPerView={isMobile ? 1 : isTablet ? 2 : 3}
                    >
                        {reviews?.map((review, i) => (
                            <SwiperSlide key={i}>
                                <ReviewCard
                                    hideReplies={true}
                                    keepMinimal={true}
                                    maxBodyLength={100}
                                    review={review}
                                    displayCompanyName={true}
                                    fixedHeight={185}
                                />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                    <div className="mb-2 flex w-full justify-center gap-2">
                        <button
                            type="button"
                            className="inline-flex w-[50px] items-center justify-center rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground transition hover:bg-primary/90"
                            onClick={() => sliderRef.current?.slidePrev()}
                        >&lt; Prev</button>
                        <button
                            type="button"
                            className="inline-flex w-[50px] items-center justify-center rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground transition hover:bg-primary/90"
                            onClick={() => sliderRef.current?.slideNext()}
                        >&gt; Next</button>
                    </div>
                    <p className="mt-0 text-center text-sm">
                        Rated <strong>{getRatingWord(group?.reviews?.stars)}</strong>&nbsp;
                        <StarRating value={group?.reviews?.stars} size="footer" />
                        &nbsp;from{' '}
                        <span
                            className="cursor-pointer"
                            onClick={() => window.open(`${group?.country?.urls?.rpp}/group/${group?.slug}`)}
                        >
                            <u>{group?.reviews?.count} reviews</u>
                        </span>
                        . Powered by{' '}
                        <img
                            src="/workingfeedback-logo.png"
                            className="-mt-1 inline-block h-[25px] align-middle"
                            alt="Working Feedback"
                        />
                    </p>
                </>
            )}
        </div>
    );
}
